import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { InputGroup, Button, VStack, Spinner, Image, Input } from '@chakra-ui/react'
import { Icon } from '../icon'
import theme from '../../styles'
import { getCookie, setCookie } from '../../scenarios'
import { mapProvince, formatPostalCode, createProvinceObject, provinceObjectType } from '../../scenarios/utils'
import { useSelector } from 'react-redux'
import { selectAnswers, selectScenario, selectScenarioIsWaitingForAnswer } from '../../state'
import { useTranslation } from 'react-i18next'
import { getLocale } from '../../i18n/locale'
import { LoadingNewAnswer } from '../spinners/loading-new-answer'
import { cardImageMap } from '../../state/interfaces'

export interface SelectProps {
  onChange?: (values: string[]) => void
  placeholder?: string
  grandImage?: string
}

const addressComponentsForm: Record<string, string> = {
  street_number: 'short_name',
  route: 'long_name',
  locality: 'long_name',
  administrative_area_level_1: 'short_name',
  country: 'long_name',
  postal_code: 'short_name',
}

const EMPTY_ADDRESS_COMPONENTS: Record<string, string> = {
  street_number: '',
  route: '',
  locality: '',
  administrative_area_level_1: '',
  country: '',
  postal_code: '',
}

const GOOGLE_MAPS_SCRIPT_ID = 'question-google-maps-places-script'
const AUTOCOMPLETE_FALLBACK_DELAY_MS = 1500
const GOOGLE_PLACES_WAIT_TIMEOUT_MS = 2000

let googleMapsPlacesPromise: Promise<void> | null = null

type AddressComponent = Record<string, string | Array<string>>
type GooglePlace = {
  address_components?: AddressComponent[]
  formatted_address?: string
  place_id?: string
}

type GoogleMapsAutocompleteListener = {
  remove: () => void
}

type GoogleMapsAutocomplete = {
  addListener: (eventName: string, handler: () => void) => GoogleMapsAutocompleteListener
  getPlace: () => GooglePlace
}

type GoogleMapsWindow = Window &
  typeof globalThis & {
    google?: {
      maps?: {
        places?: {
          Autocomplete?: new (input: HTMLInputElement, options?: Record<string, unknown>) => GoogleMapsAutocomplete
        }
      }
    }
  }

const getGoogleMapsWindow = (): GoogleMapsWindow | null => {
  if (typeof window === 'undefined') {
    return null
  }

  return window as GoogleMapsWindow
}

const isGooglePlacesAvailable = () => {
  return Boolean(getGoogleMapsWindow()?.google?.maps?.places?.Autocomplete)
}

const loadGoogleMapsPlacesScript = (apiKey: string | undefined, language: string) => {
  if (isGooglePlacesAvailable()) {
    return Promise.resolve()
  }

  if (googleMapsPlacesPromise) {
    return googleMapsPlacesPromise
  }

  if (!apiKey) {
    return Promise.reject(new Error('Missing Google autocomplete key'))
  }

  googleMapsPlacesPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID) as HTMLScriptElement | null

    if (existingScript) {
      if (isGooglePlacesAvailable()) {
        resolve()
        return
      }

      if (existingScript.dataset.loaded === 'true') {
        googleMapsPlacesPromise = null
        reject(new Error('Google Maps Places script loaded without Autocomplete'))
        return
      }

      existingScript.addEventListener('load', () => resolve(), { once: true })
      existingScript.addEventListener(
        'error',
        () => {
          googleMapsPlacesPromise = null
          reject(new Error('Failed to load Google Maps Places script'))
        },
        { once: true },
      )
      return
    }

    const script = document.createElement('script')
    const params = new URLSearchParams({
      key: apiKey,
      libraries: 'places',
      language,
    })

    script.id = GOOGLE_MAPS_SCRIPT_ID
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`
    script.async = true
    script.defer = true

    script.addEventListener(
      'load',
      () => {
        script.dataset.loaded = 'true'
        resolve()
      },
      { once: true },
    )
    script.addEventListener(
      'error',
      () => {
        googleMapsPlacesPromise = null
        reject(new Error('Failed to load Google Maps Places script'))
      },
      { once: true },
    )

    document.body.appendChild(script)
  })

  return googleMapsPlacesPromise
}

const waitForGooglePlacesAvailability = () => {
  if (isGooglePlacesAvailable()) {
    return Promise.resolve()
  }

  return new Promise<void>((resolve, reject) => {
    const startedAt = Date.now()

    const checkAvailability = () => {
      if (isGooglePlacesAvailable()) {
        resolve()
        return
      }

      if (Date.now() - startedAt >= GOOGLE_PLACES_WAIT_TIMEOUT_MS) {
        reject(new Error('Google Maps Places is unavailable'))
        return
      }

      window.setTimeout(checkAvailability, 50)
    }

    checkAvailability()
  })
}

const shouldInitializeOnKey = (key: string) => {
  return key.length === 1 || key === 'Backspace' || key === 'Delete'
}

export const GoogleAutocomplete: FC<SelectProps> = ({ placeholder, onChange, grandImage }) => {
  const [rawAddress, setRawAddress] = useState('')
  const [placeId, setPlaceId] = useState('')
  const [itemsAlign, setItemsAlign] = useState('end')
  const [addressComponents, setAddressComponents] = useState<Record<string, string>>(EMPTY_ADDRESS_COMPONENTS)
  const [provinceObject, setProvinceObject] = useState<provinceObjectType | null>({
    name: {
      en: '',
      fr: '',
    },
    prepositions: {
      en: '',
      fr: '',
    },
  })
  const [isAutocompleteReady, setIsAutocompleteReady] = useState(false)
  const [isAutocompleteUnavailable, setIsAutocompleteUnavailable] = useState(false)
  const [shouldInitializeAutocomplete, setShouldInitializeAutocomplete] = useState(false)
  const [inputInUse, setInputInUse] = useState(false)

  const answers = useSelector(selectAnswers)
  const { t } = useTranslation()
  const locale = getLocale()
  const scenario = useSelector(selectScenario)
  const scenarioIsWaitingForAnswer = useSelector(selectScenarioIsWaitingForAnswer)
  const showCancelButton = scenario!.id === 'internet-f' || scenario!.id === 'internet-g'
  const image = useMemo(() => (grandImage ? cardImageMap[grandImage] : ''), [grandImage])
  const inputElement = useRef<HTMLInputElement>(null)
  const autocompleteRef = useRef<GoogleMapsAutocomplete | null>(null)
  const autocompleteListenerRef = useRef<GoogleMapsAutocompleteListener | null>(null)

  useEffect(() => {
    if (getCookie('internetSearchPostalcode')) {
      answers &&
        answers['address-location'] &&
        answers['address-location'].includes('finishScenario') &&
        setItemsAlign('center')
    }
  }, [])

  useEffect(() => {
    if (answers['province'] && answers['province']![0]) {
      setProvinceObject(createProvinceObject(answers['province']![0]))
    }
  }, [answers['province']])

  const extractAddressComponents = (nextAddressComponents: Record<string, string | Array<string>>[]) => {
    const address: Record<string, string> = {
      street_number: '',
      route: '',
      locality: '',
      administrative_area_level_1: '',
      country: '',
      postal_code: '',
    }

    for (let i = 0; i < nextAddressComponents.length; i++) {
      const addressType = nextAddressComponents[i].types[0]
      if (addressComponentsForm[addressType]) {
        address[addressType] = (
          nextAddressComponents[i][addressComponentsForm[addressType]] as string
        ).toLocaleLowerCase()
      }
    }

    setAddressComponents(address)
    saveAddressComponents(address)
  }

  const removeAccents = (str: string) => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  }

  const saveAddressComponents = ({
    street_number,
    route,
    locality,
    administrative_area_level_1,
    country,
    postal_code,
  }: Record<string, string>) => {
    setCookie('internetSearchCountry', removeAccents(country))
    setCookie('internetSearchLocality', removeAccents(locality))
    setCookie('internetSearchStreetName', removeAccents(route))
    setCookie('internetSearchStreetNumber', street_number)
    setCookie('internetSearchPostalcode', formatPostalCode(postal_code))
    setCookie('internetSearchProvince', mapProvince(administrative_area_level_1))
    setCookie('province', mapProvince(administrative_area_level_1))
  }

  const clearSavedAddressComponents = () => {
    setCookie('internetSearchCountry', '')
    setCookie('internetSearchLocality', '')
    setCookie('internetSearchStreetName', '')
    setCookie('internetSearchStreetNumber', '')
    setCookie('internetSearchPostalcode', '')
    setCookie('internetSearchProvince', '')
    setCookie('province', '')
  }

  const resetSelectedAddress = () => {
    setPlaceId('')
    setAddressComponents(EMPTY_ADDRESS_COMPONENTS)
    clearSavedAddressComponents()
  }

  const handlePlaceSelected = (place: GooglePlace | null) => {
    if (!place) {
      return
    }

    if (place.place_id) {
      setPlaceId(place.place_id)
    }

    if (place.address_components) {
      extractAddressComponents(place.address_components)
    }

    setRawAddress(place.formatted_address || inputElement.current?.value || '')
    setInputInUse(true)
  }

  const attachAutocomplete = () => {
    if (autocompleteRef.current || !inputElement.current) {
      return
    }

    const Autocomplete = getGoogleMapsWindow()?.google?.maps?.places?.Autocomplete
    if (!Autocomplete) {
      throw new Error('Google Maps Places autocomplete is unavailable')
    }

    autocompleteRef.current = new Autocomplete(inputElement.current, {
      types: ['geocode'],
      fields: ['address_components', 'formatted_address', 'place_id'],
      componentRestrictions: { country: 'ca' },
    })

    autocompleteListenerRef.current = autocompleteRef.current.addListener('place_changed', () => {
      handlePlaceSelected(autocompleteRef.current?.getPlace() || null)
    })
  }

  useEffect(() => {
    setCookie('placeId', placeId)
  }, [placeId])

  useEffect(() => {
    if (!shouldInitializeAutocomplete || isAutocompleteReady || isAutocompleteUnavailable) {
      return
    }

    let isCancelled = false
    const fallbackTimer = window.setTimeout(() => {
      if (!isCancelled && !autocompleteRef.current) {
        setIsAutocompleteUnavailable(true)
      }
    }, AUTOCOMPLETE_FALLBACK_DELAY_MS)

    loadGoogleMapsPlacesScript(process.env.REACT_APP_GOOGLE_AUTOCOMPLETE_KEY, locale)
      .then(() => waitForGooglePlacesAvailability())
      .then(() => {
        if (isCancelled) {
          return
        }

        attachAutocomplete()
        window.clearTimeout(fallbackTimer)
        setIsAutocompleteReady(true)
        setIsAutocompleteUnavailable(false)
      })
      .catch(() => {
        if (isCancelled) {
          return
        }

        window.clearTimeout(fallbackTimer)
        setIsAutocompleteUnavailable(true)
      })

    return () => {
      isCancelled = true
      window.clearTimeout(fallbackTimer)
    }
  }, [isAutocompleteReady, isAutocompleteUnavailable, locale, shouldInitializeAutocomplete])

  useEffect(() => {
    return () => {
      autocompleteListenerRef.current?.remove()
    }
  }, [])

  const initializeAutocomplete = () => {
    if (!shouldInitializeAutocomplete) {
      setShouldInitializeAutocomplete(true)
    }

    setInputInUse(true)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextAddress = event.target.value

    initializeAutocomplete()

    if (placeId && nextAddress !== rawAddress) {
      resetSelectedAddress()
    }

    setRawAddress(nextAddress)
  }

  const hasInputValue = rawAddress.trim().length > 0
  const canSubmitAddress = hasInputValue && (Boolean(placeId) || isAutocompleteUnavailable)

  const submitAddress = () => {
    if (scenarioIsWaitingForAnswer || !canSubmitAddress) {
      return
    }

    if (!placeId) {
      clearSavedAddressComponents()
    }

    onChange && onChange([rawAddress, ...Object.values(addressComponents)])
  }

  return (
    <VStack spacing={0} alignItems="center" width={'full'} maxWidth={650}>
      <VStack spacing={6} marginY={8} width={'full'} maxWidth={650} alignItems={itemsAlign}>
        {answers && answers['address-location'] && answers['address-location'].includes('finishScenario') ? (
          <Spinner />
        ) : (
          <InputGroup
            display="grid"
            alignItems="center"
            outline="none"
            borderRadius="md"
            variant="none"
            fontSize={{ base: 16, md: 22 }}
            paddingLeft={4}
            layerStyle={theme.LayerStyles.INPUT}
            borderColor={inputInUse ? 'green.200' : 'grey'}
            autoFocus
          >
            <Input
              ref={inputElement}
              aria-label={placeholder}
              placeholder={placeholder}
              value={rawAddress}
              variant="unstyled"
              h="68px"
              pl="16px"
              pr="65px"
              w="100%"
              fontSize={{ base: 18, md: 22 }}
              border="none"
              backgroundColor="transparent"
              _placeholder={{ color: 'ashGrey' }}
              autoFocus
              onChange={handleInputChange}
              onClick={(event) => {
                if (!scenarioIsWaitingForAnswer) {
                  event.stopPropagation()
                  initializeAutocomplete()
                }
              }}
              onFocus={() => setInputInUse(true)}
              onBlur={() => setInputInUse(false)}
              onKeyDown={(event) => {
                setInputInUse(true)

                if (!scenarioIsWaitingForAnswer && shouldInitializeOnKey(event.key)) {
                  initializeAutocomplete()
                }

                if (!scenarioIsWaitingForAnswer && event.key === 'Enter' && canSubmitAddress) {
                  event.preventDefault()
                  submitAddress()
                }
              }}
              onMouseEnter={() => {
                setInputInUse(true)
              }}
              onMouseLeave={() => {
                setInputInUse(hasInputValue)
              }}
            />
            {shouldInitializeAutocomplete && !isAutocompleteReady && !isAutocompleteUnavailable && (
              <Spinner size="sm" position="absolute" top="50%" right="72px" transform="translateY(-50%)" />
            )}
            {canSubmitAddress && (
              <Button
                role={'button'}
                position="absolute"
                borderRadius="md"
                layerStyle={theme.LayerStyles.BTN_INPUT_PILL}
                onClick={submitAddress}
              >
                {scenarioIsWaitingForAnswer ? <LoadingNewAnswer /> : <Icon icon="arrow-right" color="white" />}
              </Button>
            )}
          </InputGroup>
        )}
        {showCancelButton && answers && !answers['address-location'] && (
          <Button
            role={'button'}
            borderRadius={'lg'}
            borderColor={'#888888'}
            onClick={() => {
              setItemsAlign('center')
              onChange && onChange(['No address'])
            }}
            fontSize={{ base: 14, md: 22 }}
            marginTop={10}
            textColor={'#888888'}
            tabIndex={0}
            _focus={{ borderColor: 'purple' }}
            _hover={{ borderColor: 'purple' }}
          >
            {t('Skip-and-search-for-all')} {provinceObject?.prepositions[locale]}
            {provinceObject?.name[locale]}
          </Button>
        )}
      </VStack>
      <Image src={image} alt="image" width={['60%', '45%']} />
    </VStack>
  )
}
